import type { HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { LANGUAGES } from '../../constants';
import type { LanguageCode, WebsiteTranslatorSettings } from '../../types';
import { checkBackground } from '../../utils/check-background';
import { calculateColumnGap } from './background';
import { clickableZone } from './container-mixins';
import { NameContainer } from './name';
import { slash } from './slash';

type ContainerProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & {
    languageCode: LanguageCode;
    itemsLength?: number;
    withSlash?: boolean;
    isDropdown?: boolean;
    isOpen?: boolean;
    onClick?(): void;
  }
>;

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      children,
      itemsLength = 1,
      withSlash = false,
      languageCode,
      isDropdown = false,
      isOpen,
      onClick,
      role,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const {
      textColor,
      widgetSize,
      style,
      displayDeactivation,
      cornerRadius,
      ...settings
    } = useSettings();

    const hasBackground = checkBackground(settings);

    const { originalName } = LANGUAGES[languageCode];
    const ariaLabel = `${originalName} (${languageCode})`;

    return (
      <Wrapper
        ref={forwardingRef}
        _withSlash={withSlash}
        _hasBackground={hasBackground}
        _textColor={textColor}
        _widgetSize={widgetSize}
        _columnGap={calculateColumnGap({ style, itemsLength })}
        _displayDeactivation={displayDeactivation}
        _cornerRadius={cornerRadius}
        onClick={onClick}
        role={role ?? (onClick ? 'button' : undefined)}
        aria-label={ariaLabel}
        aria-haspopup={isDropdown ? 'listbox' : undefined}
        aria-expanded={isDropdown ? isOpen : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? getHandleActionKeyDown(onClick) : undefined}
        {...forwardingProps}
      >
        {children}
      </Wrapper>
    );
  }
);

const Wrapper = styled.div<{
  _withSlash: boolean;
  _hasBackground: boolean;
  _textColor: WebsiteTranslatorSettings['textColor'];
  _widgetSize: WebsiteTranslatorSettings['widgetSize'];
  _columnGap: number;
  _cornerRadius?: number;
  _displayDeactivation?: boolean;
}>`
  display: inline-flex;
  gap: 8px;
  align-items: center;
  width: fit-content;
  position: relative;
  border-radius: inherit;
  cursor: pointer;
  ${({ _textColor }) => _textColor && `color: ${_textColor};`};

  &:hover ${NameContainer}, &:focus-visible ${NameContainer} {
    text-decoration: underline;
  }

  &:focus-visible {
    position: relative;
    overflow: hidden;
    outline: 2px solid rgb(0, 95, 204) !important;
    outline-offset: 2px;
    border-radius: ${({ _cornerRadius }) =>
      _cornerRadius ? `${_cornerRadius - 6}px` : 'inherit'};
    box-shadow: 0px 0px 0px 2px rgb(255, 255, 255);
  }

  ${({ _withSlash, _widgetSize, _textColor }) =>
    _withSlash && slash({ _widgetSize, _textColor })}

  ${clickableZone()}
`;
